import axios from 'axios';
import { GitProvider } from 'consts/git';
import { Endpoint } from 'types/url';
import { buildSearchParams } from 'utils/url';
import { BaseHttpClient } from '../BaseHttpClient';
import { injectCommonInterceptors } from '../interceptors';
import {
  BulkRepositoriesRequest,
  BitbucketWizardSelectionOptionsResponse,
  BitbucketConfigResponse,
  BitbucketCreateInstallationRequest,
  BitbucketCreateInstallationResponse,
  BitbucketInstallWebhooksRequest,
  BitbucketInstallWebhooksResponse,
  BitbucketReinstallWebhooksRequest,
  BitbucketReinstallWebhooksResponse,
  BitbucketOAuthSessionResponse,
  BitbucketOAuthSessionCreateRequest,
  BitbucketOAuthSessionCreateResponse,
  GerritCreateInstallationRequest,
  GerritCreateInstallationResponse,
  GerritImportStatusResponse,
  DeleteGitInstallationResponse,
  GetGitInstallationsRequest,
  GetGitInstallationsResponse,
  GetGitRepositoriesRequest,
  GetGitRepositoriesResponse,
  GetWebhookUrlResponse,
  GitRepository,
  CreateMarketplaceMTGithubInstallationRequest,
  CreateMarketplaceMTGithubInstallationResponse,
  HostingType,
  InitiateMTGithubInstallationResponse,
  InstallationRepositoriesRequest,
  InstallationRepositoriesResponse,
  InstallationsCountsResponse,
  RepositoryOperationRequest,
  RepositoryOperationResponse,
  TaskTrackerSummaryResponse,
  TestConnectionResponse,
} from './api.schema';
import { BASE_URL } from './consts';
import type { BulkResponse } from '../../../components/BulkActions';

const axiosRepositoryInstance = axios.create({
  baseURL: BASE_URL,
});

injectCommonInterceptors(axiosRepositoryInstance);

export class RepositoryClient extends BaseHttpClient {
  constructor() {
    super(axiosRepositoryInstance);
  }

  // Singleton instance for access without instantiation
  static override instance = new RepositoryClient();

  static async getGitInstallations(params?: GetGitInstallationsRequest) {
    const searchParams = buildSearchParams(params);

    return await RepositoryClient.get<GetGitInstallationsResponse>(
      'git_integration/v1/installations' as Endpoint,
      { params: searchParams },
    );
  }
  static async deleteGitInstallation(installationId: string) {
    const endpoint = `git_integration/v1/installations/${installationId}`;

    return await RepositoryClient.delete<DeleteGitInstallationResponse>(
      endpoint as Endpoint,
    );
  }

  static async getGitRepositories(params?: GetGitRepositoriesRequest) {
    const searchParams = buildSearchParams(params);

    return await RepositoryClient.get<GetGitRepositoriesResponse>(
      'git_integration/v1/repositories' as Endpoint,
      { params: searchParams },
    );
  }

  /** Applies one capability toggle to a selection of repositories in one call. */
  static async bulkRepositoriesOperation(payload: BulkRepositoriesRequest) {
    return await RepositoryClient.post<BulkResponse>(
      'git_integration/v1/repositories/bulk' as Endpoint,
      payload,
    );
  }

  static async getGitRepositoryById(repositoryId: string) {
    const endpoint = `git_integration/v1/repositories/${repositoryId}`;

    return await RepositoryClient.get<GitRepository>(endpoint as Endpoint);
  }

  /**
   * Admin-only; 202 means enqueued, not started. Kill-switched off returns 503.
   * @param repositoryId - The repository the operation runs against.
   * @param payload - Which operation to enqueue.
   * @returns The enqueue acknowledgement.
   */
  static async requestRepositoryOperation(
    repositoryId: string,
    payload: RepositoryOperationRequest,
  ) {
    const endpoint = `git_integration/v1/repositories/${encodeURIComponent(
      repositoryId,
    )}/operations`;

    return await RepositoryClient.post<RepositoryOperationResponse>(
      endpoint as Endpoint,
      payload,
    );
  }

  static async testGitInstallationConnection(installationId: number) {
    const searchParams = buildSearchParams({
      installation_id: installationId,
    });

    return await RepositoryClient.get<TestConnectionResponse>(
      'git_integration/v1/installation/test-connection' as Endpoint,
      { params: searchParams },
    );
  }

  static async getGitIntegrationWebhookUrl(provider: GitProvider) {
    const searchParams = buildSearchParams({ provider });

    return await RepositoryClient.get<GetWebhookUrlResponse>(
      'git_integration/v1/webhook-url' as Endpoint,
      { params: searchParams },
    );
  }

  static async getInstallationsCounts(hostingType?: HostingType) {
    const searchParams = buildSearchParams({
      hosting_type: hostingType,
    });

    return await RepositoryClient.get<InstallationsCountsResponse>(
      'git_integration/v1/installations/counts' as Endpoint,
      { params: searchParams },
    );
  }

  static async getTaskTrackerSummary() {
    return await RepositoryClient.get<TaskTrackerSummaryResponse>(
      'git_integration/v1/tasks/summary' as Endpoint,
    );
  }

  static async syncGitInstallation(installationId: string) {
    const endpoint = `git_integration/v1/installations/${installationId}/sync`;

    return await RepositoryClient.post(endpoint as Endpoint, undefined);
  }

  /**
   * Adds repositories to an existing installation via the generic
   * installation-repositories endpoint.
   * Single source of truth for the add-repositories URL/body — change it here only.
   */
  static async addInstallationRepositories(
    request: InstallationRepositoriesRequest,
  ) {
    const endpoint = `git_integration/v1/installations/${request.installationId}/repositories`;

    return await RepositoryClient.post<InstallationRepositoriesResponse>(
      endpoint as Endpoint,
      // The endpoint contract is provider_repo_ids: string[]; send the native
      // (numeric GitLab) ids as strings so the request validates.
      { provider_repo_ids: request.providerRepoIds.map(String) },
    );
  }

  /**
   * Removes repositories from an existing installation via the generic
   * installation-repositories endpoint.
   * Single source of truth for the remove-repositories URL/body — change it here only.
   */
  static async removeInstallationRepositories(
    request: InstallationRepositoriesRequest,
  ) {
    const endpoint = `git_integration/v1/installations/${request.installationId}/repositories`;

    return await RepositoryClient.delete<InstallationRepositoriesResponse>(
      endpoint as Endpoint,
      // Contract is provider_repo_ids: string[] — stringify the native ids.
      { data: { provider_repo_ids: request.providerRepoIds.map(String) } },
    );
  }

  static async bitbucketGetConfig() {
    return await RepositoryClient.get<BitbucketConfigResponse>(
      'git_integration/v1/bitbucket/config' as Endpoint,
    );
  }

  static async bitbucketOAuthSessionCreate(
    payload: BitbucketOAuthSessionCreateRequest,
  ) {
    return await RepositoryClient.post<BitbucketOAuthSessionCreateResponse>(
      'git_integration/v1/bitbucket/oauth/session' as Endpoint,
      payload,
    );
  }

  static async bitbucketGetOAuthSession(sessionId: string) {
    return await RepositoryClient.get<BitbucketOAuthSessionResponse>(
      `git_integration/v1/bitbucket/oauth/session/${sessionId}` as Endpoint,
    );
  }

  static async bitbucketCreateInstallation(
    payload: BitbucketCreateInstallationRequest,
  ) {
    return await RepositoryClient.post<BitbucketCreateInstallationResponse>(
      'git_integration/v1/bitbucket/installations' as Endpoint,
      payload,
    );
  }

  static async bitbucketGetWizardSelectionOptions(
    bitbucketWorkspaceSlug: string,
  ) {
    return await RepositoryClient.get<BitbucketWizardSelectionOptionsResponse>(
      `git_integration/v1/bitbucket/repositories?bitbucket_workspace_slug=${encodeURIComponent(
        bitbucketWorkspaceSlug,
      )}` as Endpoint,
    );
  }

  static async bitbucketInstallWebhooks(
    payload: BitbucketInstallWebhooksRequest,
  ) {
    return await RepositoryClient.post<BitbucketInstallWebhooksResponse>(
      'git_integration/v1/bitbucket/webhooks' as Endpoint,
      payload,
    );
  }

  static async bitbucketReinstallMissingWebhooks(
    payload: BitbucketReinstallWebhooksRequest,
  ) {
    return await RepositoryClient.post<BitbucketReinstallWebhooksResponse>(
      'git_integration/v1/bitbucket/webhooks/reinstall-missing' as Endpoint,
      payload,
    );
  }

  static async gerritCreateInstallation(
    payload: GerritCreateInstallationRequest,
  ) {
    return await RepositoryClient.post<GerritCreateInstallationResponse>(
      'git_integration/v1/gerrit/installations' as Endpoint,
      payload,
    );
  }

  /** Live status of the Gerrit auto-import started when the installation is created. */
  static async gerritGetImportStatus(installationId: string) {
    const endpoint = `git_integration/v1/gerrit/installations/${encodeURIComponent(
      installationId,
    )}/import-status`;

    return await RepositoryClient.get<GerritImportStatusResponse>(
      endpoint as Endpoint,
    );
  }

  static async initiateMTGithubInstallation(frontendRedirectUrl: string) {
    return await RepositoryClient.post<InitiateMTGithubInstallationResponse>(
      'git_integration/v1/mt/installation/initiate' as Endpoint,
      { frontend_redirect_url: frontendRedirectUrl },
    );
  }

  static async createMarketplaceMTGithubInstallation(
    payload: CreateMarketplaceMTGithubInstallationRequest,
  ) {
    return await RepositoryClient.post<CreateMarketplaceMTGithubInstallationResponse>(
      'git_integration/v1/mt/installation/create' as Endpoint,
      payload,
    );
  }
}
